import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that manages the FAQ search functionality.
 * Provides real-time filtering of FAQ items based on user input.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content – The results container element.
 * @property {HTMLElement} noResults – The no results container element.
 *
 * @extends Component<Refs>
 */
export class FaqSearch extends Component {
  requiredRefs = ['content', 'noResults'];

  /** @type {Element | null} */
  #originalContent = null;

  /** @type {Element | null} */
  #accordionContainer = null;

  /** @type {Element[]} */
  #answers = [];

  /** @type {boolean} */
  #isInitialized = false;

  /**
   * Debounced search handler to prevent excessive filtering during typing.
   * @type {Function}
   */
  search = debounce((event) => {
    const searchValue = event.target?.value || '';
    this.#filterAnswers(searchValue);
  }, 300);

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  /**
   * Initializes the FAQ search component by cloning and storing the original content.
   */
  #initialize() {
    if (this.#isInitialized) return;

    try {
      this.#originalContent = this.#cloneOriginalContent();
      this.#accordionContainer = this.#extractAccordionContainer();
      this.#answers = this.#extractAnswers();
      this.#isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize FAQ search:', error);
    }
  }

  /**
   * Clones the original content for restoration when search is cleared.
   * @returns {Element | null} The cloned content element.
   */
  #cloneOriginalContent() {
    if (!this.refs.content) {
      console.warn('FAQ search: content ref not found');
      return null;
    }
    return /** @type {Element} */ (this.refs.content.cloneNode(true));
  }

  /**
   * Extracts the accordion container template from the original content.
   * @returns {Element | null} The accordion container element.
   */
  #extractAccordionContainer() {
    if (!this.#originalContent) return null;
    
    const accordion = this.#originalContent.querySelector('.accordion');
    if (!accordion) {
      console.warn('FAQ search: accordion container not found');
      return null;
    }
    return /** @type {Element} */ (accordion.cloneNode(false));
  }

  /**
   * Extracts all FAQ answer elements from the original content.
   * @returns {Element[]} Array of answer elements.
   */
  #extractAnswers() {
    if (!this.#originalContent) return [];
    
    const answers = this.#originalContent.querySelectorAll('accordion-custom');
    return Array.from(answers);
  }

  /**
   * Filters FAQ answers based on the search value.
   * @param {string} searchValue - The search term to filter by.
   */
  #filterAnswers(searchValue) {
    if (!this.#isInitialized) {
      console.warn('FAQ search: component not initialized');
      return;
    }

    const trimmedValue = searchValue.trim().toLowerCase();
    this.#clearContent();

    if (trimmedValue.length === 0) {
      this.#restoreOriginalContent();
      return;
    }

    this.#displayFilteredResults(trimmedValue);
  }

  /**
   * Clears all content from the results container.
   */
  #clearContent() {
    if (!this.refs.content) return;

    this.refs.noResults.setAttribute('hidden', '');
    
    // More efficient way to clear child nodes
    this.refs.content.innerHTML = '';
  }

  /**
   * Restores the original content when search is cleared.
   */
  #restoreOriginalContent() {
    if (!this.#originalContent || !this.refs.content) return;
    
    this.refs.content.appendChild(this.#originalContent.cloneNode(true));
  }

  /**
   * Displays filtered results based on the search term.
   * @param {string} searchTerm - The search term to filter by.
   */
  #displayFilteredResults(searchTerm) {
    if (!this.#accordionContainer || !this.refs.content) return;

    const filteredContainer = this.#accordionContainer.cloneNode(false);
    const matchingAnswers = this.#getMatchingAnswers(searchTerm);

    if (matchingAnswers.length === 0) {
      this.refs.noResults.removeAttribute('hidden');
      return;
    }

    matchingAnswers.forEach(answer => {
      filteredContainer.appendChild(answer.cloneNode(true));
    });

    this.refs.content.appendChild(filteredContainer);
  }

  /**
   * Gets all answers that match the search term.
   * @param {string} searchTerm - The search term to match against.
   * @returns {Element[]} Array of matching answer elements.
   */
  #getMatchingAnswers(searchTerm) {
    return this.#answers.filter(answer => {
      const answerText = answer.textContent?.toLowerCase() || '';
      return answerText.includes(searchTerm);
    });
  }

  /**
   * Resets the search to show all original content.
   * @public
   */
  reset() {
    this.#clearContent();
    this.#restoreOriginalContent();
  }
}

if (!customElements.get('faq-search')) {
  customElements.define('faq-search', FaqSearch);
}
